import {
    DASHBOARD_SECTIONS,
    OPERATIONAL_METRIC_VALUE_TYPES,
    resolveMetricDashboardSection,
} from '../constants/operationalMetricCatalog';
import { formatDateIsoLocal } from './deliveryDateRange';

/**
 * Resolves customerId query param for operational dashboard (same rules as delivery status counts).
 * @param {{ isTransportCompany: boolean, selectedCustomerId: string | null, isManagingOwnData: boolean }} ctx
 * @returns {string | undefined}
 */
export function resolveDashboardCustomerId({ isTransportCompany, selectedCustomerId, isManagingOwnData }) {
    if (!isTransportCompany) {
        if (selectedCustomerId != null && String(selectedCustomerId).trim() !== '') {
            return String(selectedCustomerId);
        }
        return undefined;
    }
    if (isManagingOwnData || selectedCustomerId == null || String(selectedCustomerId).trim() === '') {
        return undefined;
    }
    return String(selectedCustomerId);
}

/**
 * Fills missing days in a time series with value 0 (inclusive range).
 * @param {Array<{ date: string, value: number }>} points
 * @param {string} startDate yyyy-MM-dd
 * @param {string} endDate yyyy-MM-dd
 * @returns {Array<{ date: string, value: number }>}
 */
export function fillTimeSeriesGaps(points, startDate, endDate) {
    if (!startDate || !endDate) {
        return Array.isArray(points) ? points.map(normalizeTimeSeriesPoint) : [];
    }
    const byDate = new Map();
    (points || []).forEach((p) => {
        const normalized = normalizeTimeSeriesPoint(p);
        if (normalized.date) {
            byDate.set(normalized.date, normalized.value);
        }
    });

    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
        return Array.isArray(points) ? points.map(normalizeTimeSeriesPoint) : [];
    }

    const result = [];
    const cursor = new Date(start);
    while (cursor <= end) {
        const iso = formatDateIsoLocal(cursor);
        result.push({
            date: iso,
            value: byDate.has(iso) ? byDate.get(iso) : 0,
        });
        cursor.setDate(cursor.getDate() + 1);
    }
    return result;
}

/**
 * @param {{ date?: string, value?: number }} point
 */
export function normalizeTimeSeriesPoint(point = {}) {
    const value = Number(point.value);
    return {
        date: point.date ? String(point.date).slice(0, 10) : '',
        value: Number.isFinite(value) ? value : 0,
    };
}

/**
 * Groups normalized metrics into dashboard layout sections.
 * VOLUME_SERIES appears as KPI (total) and series (points).
 * @param {Array<{ key: string, valueType: string, data?: * }>} metrics
 */
export function groupMetricsForLayout(metrics = []) {
    const kpis = [];
    const statusMetrics = [];
    const seriesMetrics = [];
    const rankingMetrics = [];
    const unknown = [];

    metrics.forEach((metric) => {
        switch (metric.valueType) {
            case OPERATIONAL_METRIC_VALUE_TYPES.SCALAR:
            case OPERATIONAL_METRIC_VALUE_TYPES.MONEY:
            case OPERATIONAL_METRIC_VALUE_TYPES.RATE:
                kpis.push(metric);
                break;
            case OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES:
                kpis.push({ ...metric, layoutSlot: 'volumeTotal' });
                seriesMetrics.push({ ...metric, layoutSlot: 'volumePoints' });
                break;
            case OPERATIONAL_METRIC_VALUE_TYPES.STATUS_COUNT_LIST:
                statusMetrics.push(metric);
                break;
            case OPERATIONAL_METRIC_VALUE_TYPES.TIME_SERIES:
                seriesMetrics.push(metric);
                break;
            case OPERATIONAL_METRIC_VALUE_TYPES.RANKING:
                rankingMetrics.push(metric);
                break;
            default:
                unknown.push(metric);
                break;
        }
    });

    return { kpis, statusMetrics, seriesMetrics, rankingMetrics, unknown };
}

function layoutHasMetrics(layout) {
    return layout.kpis.length > 0
        || layout.statusMetrics.length > 0
        || layout.seriesMetrics.length > 0
        || layout.rankingMetrics.length > 0
        || layout.unknown.length > 0;
}

/**
 * Agrupa métricas nas seções do painel (Entregas, Financeiro),
 * preservando o layout interno de cada seção.
 * @param {Array<{ key: string, valueType: string, data?: * }>} metrics
 */
export function groupMetricsByDashboardSection(metrics = []) {
    const buckets = {
        entregas: [],
        financeiro: [],
    };

    metrics.forEach((metric) => {
        const sectionId = resolveMetricDashboardSection(metric.key);
        buckets[sectionId].push(metric);
    });

    return DASHBOARD_SECTIONS
        .map((section) => ({
            ...section,
            ...groupMetricsForLayout(buckets[section.id]),
        }))
        .filter(layoutHasMetrics);
}

/**
 * Whether transport company must pick scope before loading dashboard data.
 * @param {{ isTransportCompany: boolean, selectedCustomerId: string | null }} ctx
 */
export function canLoadOperationalDashboard({ isTransportCompany, selectedCustomerId }) {
    if (!isTransportCompany) {
        return true;
    }
    return selectedCustomerId != null && String(selectedCustomerId).trim() !== '';
}
